import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { api, clearWorkspaceSession, post } from "./api";

export type AuthUser = {
  id: string;
  email: string;
  display_name: string;
  role: "admin" | "member";
};
type Session = {
  user: AuthUser | null;
  email_configured: boolean;
  login_mode?: "email" | "otp";
};
type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  error: string;
  emailConfigured: boolean;
  loginMode: "email" | "otp";
  refresh: () => Promise<void>;
  signedIn: (user: AuthUser) => void;
  logout: () => Promise<void>;
};
const Context = createContext<AuthState | null>(null);
export const useAuth = () => useContext(Context);

export function loginDestination() {
  const route = location.hash.slice(1);
  const destination =
    route.split("?")[0] === "/login"
      ? new URLSearchParams(route.split("?").slice(1).join("?")).get("next")
      : route;
  return destination?.startsWith("/") &&
    !destination.startsWith("//") &&
    destination !== "/" &&
    destination.split("?")[0] !== "/login"
    ? destination
    : "/scripts";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emailConfigured, setConfigured] = useState(false);
  const [loginMode, setLoginMode] = useState<"email" | "otp">("email");
  const generation = useRef(0);
  const channel = useRef<BroadcastChannel>();
  const userId = useRef<string>();
  const accept = useCallback((next: AuthUser | null) => {
    // A verified session for the same account may retain unsent edits on reload.
    // Unknown owners and account changes still clear all workspace data.
    let previousOwner = userId.current;
    try {
      previousOwner ??= localStorage.getItem("aclip-workspace-owner") || undefined;
    } catch {}
    if (!next || previousOwner !== next.id) clearWorkspaceSession();
    try {
      if (next) localStorage.setItem("aclip-workspace-owner", next.id);
      else localStorage.removeItem("aclip-workspace-owner");
    } catch {}
    userId.current = next?.id;
    setUser(next);
  }, []);
  const refresh = useCallback(async () => {
    const version = ++generation.current;
    try {
      const session = await api<Session>("/auth/session", {
        signal: AbortSignal.timeout(10_000),
      });
      if (version !== generation.current) return;
      setConfigured(session.email_configured);
      setLoginMode(session.login_mode || "otp");
      const wasSignedIn = !!userId.current;
      accept(session.user);
      setError((previous) =>
        session.user
          ? ""
          : wasSignedIn
            ? "登录已过期，请重新登录后继续。"
            : previous.startsWith("登录已过期")
              ? previous
              : "",
      );
    } catch (failure) {
      if (version !== generation.current) return;
      setError(
        failure instanceof Error
          ? failure.message
          : "登录服务暂时不可用，请重试。",
      );
      if (!userId.current) accept(null);
    } finally {
      if (version === generation.current) setLoading(false);
    }
  }, [accept]);
  useEffect(() => {
    void refresh();
    const expired = () => {
      generation.current++;
      accept(null);
      setError("登录已过期，请重新登录后继续。");
      setLoading(false);
    };
    const focus = () => {
      void refresh();
    };
    window.addEventListener("aclip-auth-required", expired);
    window.addEventListener("focus", focus);
    if (typeof BroadcastChannel !== "undefined") {
      channel.current = new BroadcastChannel("aclip-auth");
      channel.current.onmessage = () => {
        void refresh();
      };
    }
    return () => {
      generation.current++;
      window.removeEventListener("aclip-auth-required", expired);
      window.removeEventListener("focus", focus);
      channel.current?.close();
    };
  }, [accept, refresh]);
  const signedIn = useCallback(
    (next: AuthUser) => {
      generation.current++;
      accept(next);
      setError("");
      setLoading(false);
      channel.current?.postMessage("changed");
      location.hash = loginDestination();
    },
    [accept],
  );
  const logout = useCallback(async () => {
    await post("/auth/logout");
    generation.current++;
    accept(null);
    setError("");
    channel.current?.postMessage("changed");
    location.hash = "/login";
  }, [accept]);
  return (
    <Context.Provider
      value={{
        user,
        loading,
        error,
        emailConfigured,
        loginMode,
        refresh,
        signedIn,
        logout,
      }}
    >
      {children}
    </Context.Provider>
  );
}
